import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarIcon,
  VideoCameraIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { formatTimeInTimezone, getTimezoneAbbreviation } from '../utils/timezoneUtils';

export type AppointmentType = "video_call" | "phone_call" | "in_person";

export type AppointmentStatus =
  | "confirmed"
  | "pending"
  | "completed"
  | "cancelled"
  | "scheduled"
  | "no_show";

export interface Appointment {
  id: string;
  client_name: string;
  client_avatar?: string;
  client_email?: string;
  title: string;
  type: AppointmentType;
  date: Date;
  duration: number;
  status: AppointmentStatus;
  scheduled_by?: 'guide' | 'seeker' | null; // Who scheduled - opposite party can confirm
  notes?: string;
}

type ScheduleListViewProps = {
  appointments: Appointment[];
  onConfirm?: (appointmentId: string) => void;
  confirmingSessionId?: string | null;
  guideTimezone?: string;
};

const statusStyles: Record<AppointmentStatus, string> = {
  confirmed: "bg-[#2C2731] text-[#FEF6D6] border border-[#2C2731]",
  pending: "bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]",
  completed: "bg-[#F3EBCD] text-[#2C2731] border border-[#2C2731]/40",
  cancelled: "bg-[#2C2731]/10 text-[#2C2731]/80 border border-[#2C2731]/30",
  scheduled: "bg-[#2C2731] text-[#FEF6D6] border border-[#2C2731]",
  no_show: "bg-[#E8DFBD] text-[#2C2731]/80 border border-[#2C2731]/40",
};

const statusLabel: Record<AppointmentStatus, string> = {
  confirmed: "Confirmed",
  pending: "Pending",
  completed: "Completed",
  cancelled: "Cancelled",
  scheduled: "Scheduled",
  no_show: "No show",
};

const generateAvatar = (name: string) => {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((segment) => segment[0])
    .join("")
    .toUpperCase();
  const colors = [
    "bg-[#2C2731]",
    "bg-[#3A3340]",
    "bg-[#4B4353]",
    "bg-[#352F3B]",
    "bg-[#433B4A]",
    "bg-[#2E2836]",
  ];
  const colorIndex = name.length % colors.length;

  return (
    <div
      className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium text-[#FEF6D6] border border-[#FEF6D6] ${colors[colorIndex]}`}
    >
      {initials || "?"}
    </div>
  );
};

const getTypeIcon = (type: AppointmentType) => {
  switch (type) {
    case "video_call":
      return <VideoCameraIcon className="h-4 w-4" />;
    case "phone_call":
      return <PhoneIcon className="h-4 w-4" />;
    case "in_person":
      return <MapPinIcon className="h-4 w-4" />;
    default:
      return <CalendarIcon className="h-4 w-4" />;
  }
};

const formatTimeFallback = (date: Date, guideTimezone?: string) => {
  if (guideTimezone) {
    const isoString = date.toISOString();
    const time = formatTimeInTimezone(isoString, guideTimezone);
    const tz = getTimezoneAbbreviation(isoString, guideTimezone);
    return tz ? `${time} ${tz}` : time;
  }
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

const formatGroupDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const ScheduleListView: React.FC<ScheduleListViewProps> = ({ appointments, onConfirm, confirmingSessionId, guideTimezone }) => {
  const navigate = useNavigate();

  const canEnterSession = (status: AppointmentStatus) => {
    return !['completed', 'cancelled', 'no_show'].includes(status);
  };

  const sortedAppointments = [...appointments].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  const grouped = sortedAppointments.reduce<Record<string, Appointment[]>>((acc, appointment) => {
    const key = appointment.date.toDateString();
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(appointment);
    return acc;
  }, {});

  const groupedEntries = Object.entries(grouped).sort(
    ([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()
  );

  if (groupedEntries.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-[#2C2731]/40 bg-[#FEF6D6] text-[#2C2731]">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731]">
            <CalendarIcon className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium">No upcoming appointments</p>
          <p className="mt-1 text-xs text-[#2C2731]/70">
            When seekers book with you, you will see them listed here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-[#2C2731]">
      {groupedEntries.map(([dateKey, items]) => {
        const groupDate = new Date(dateKey);
        return (
          <section key={dateKey} className="rounded-xl border border-[#2C2731] bg-[#FEF6D6]">
            <header className="flex items-center justify-between border-b border-[#2C2731] bg-[#2C2731] px-4 py-3 text-[#FEF6D6]">
              <div>
                <h2 className="text-base font-medium font-recoleta">{formatGroupDate(groupDate)}</h2>
                <p className="text-xs text-[#FEF6D6]/80">
                  {items.length === 1 ? "1 appointment" : `${items.length} appointments`}
                </p>
              </div>
              <div className="flex items-center space-x-2 text-xs text-[#FEF6D6]/80">
                <ClockIcon className="h-4 w-4" />
                <span>All times in your local timezone</span>
              </div>
            </header>
            <ul className="divide-y divide-[#2C2731]/30">
              {items.map((appointment) => (
                <li key={appointment.id} className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-1 items-start gap-3">
                    {appointment.client_avatar ? (
                      <img
                        src={appointment.client_avatar}
                        alt={appointment.client_name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      generateAvatar(appointment.client_name)
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium">
                          {appointment.client_name}
                        </p>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            statusStyles[appointment.status]
                          }`}
                        >
                          {statusLabel[appointment.status]}
                        </span>
                      </div>
                      <p className="text-xs text-[#2C2731]/70">{appointment.title}</p>
                      {/* Show confirmation prompt when seeker scheduled and status is scheduled */}
                      {appointment.status === 'scheduled' && appointment.scheduled_by === 'seeker' && (
                        <div className="mt-2 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5">
                          <span className="text-xs text-blue-700">Seeker scheduled this session. Please confirm.</span>
                          {onConfirm && (
                            <button
                              onClick={() => onConfirm(appointment.id)}
                              disabled={confirmingSessionId === appointment.id}
                              className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {confirmingSessionId === appointment.id ? 'Confirming...' : 'Confirm'}
                            </button>
                          )}
                        </div>
                      )}
                      {appointment.notes && (
                        <p className="mt-2 line-clamp-2 text-xs text-[#2C2731]/70">
                          {appointment.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-start gap-2 text-sm text-[#2C2731]/80 sm:items-end">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center gap-1 text-xs text-[#2C2731]/70">
                        {getTypeIcon(appointment.type)}
                        <span>{formatTimeFallback(appointment.date, guideTimezone)}</span>
                      </div>
                      <span className="text-xs text-[#2C2731]/40">•</span>
                      <span className="text-xs text-[#2C2731]/70">
                        {appointment.duration} min
                      </span>
                    </div>
                    {canEnterSession(appointment.status) && (
                      <button
                        onClick={() => navigate(`/waiting-room?sessionId=${appointment.id}`)}
                        className="rounded-md bg-[#2C2731] px-3 py-1.5 text-xs font-medium text-[#FFFCEF] hover:bg-[#241F2A] transition-colors"
                      >
                        Enter Session
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default ScheduleListView;
